/**
 * Matrix Pricing Validation Composable
 *
 * Provides validation and dynamic min/max values for matrix pricing
 * based on other dimension values in the configuration.
 *
 * @param {Object} product - Product with pricingMatrix config
 * @param {Object} formData - Current form data with user selections
 * @returns {Object} Validation helpers and dynamic constraints
 */

import { computed } from 'vue';
import { MatrixPricingEngine } from '@shared/services/matrixPricingEngine';
import { resolveChoiceAxisValue } from '@shared/utils/choiceAxis';
import { resolveConstraintDimensionValue } from '@shared/composables/useCPQPriceCalculator';

export function useMatrixPricingValidation(product, formData) {
  const matrixEngine = new MatrixPricingEngine();
  const computedFields = (product?.computedFields || product?.computed_fields || []).filter(
    (field) => field?.id && field?.formula
  );
  const fieldIdFor = (optionId) => `${product.id}-${optionId}-input`;

  const getDimensionsArray = () => {
    const dimensions = convertedMatrix.value?.dimensions;

    if (Array.isArray(dimensions)) {
      return dimensions;
    }

    // Defensive fallback for legacy/incomplete matrix shapes where dimensions
    // can still be an object like { row, col, group }.
    if (dimensions && typeof dimensions === 'object') {
      const result = [];
      if (dimensions.row) result.push({ optionId: dimensions.row });
      if (dimensions.col) result.push({ optionId: dimensions.col });
      if (dimensions.group) result.push({ optionId: dimensions.group });
      return result;
    }

    return [];
  };

  // Convert grid to cells if needed
  const convertedMatrix = computed(() => {
    if (!product?.useMatrixPricing || !product?.pricingMatrix) {
      return null;
    }

    const converted = matrixEngine.convertGridToCells(product.pricingMatrix, product);

    return converted;
  });

  /**
   * Get the default value for a product option.
   * For number options: numberConfig.defaultValue
   * For single-choice options: the ID of the first choice with isDefault: true
   */
  const getDefaultValueForOption = (optionId) => {
    if (!product?.options) return undefined;

    const option = product.options.find((o) => o.id === optionId);
    if (!option) return undefined;

    if (option.type === 'number' && option.numberConfig?.defaultValue) {
      return option.numberConfig.defaultValue;
    }

    if (
      option.type === 'single-choice' &&
      option.choiceConfig?.choices?.length
    ) {
      const defaultChoice = option.choiceConfig.choices.find((c) => c.isDefault);
      if (defaultChoice) {
        return defaultChoice.id ||
          `${option.id}-choice-${option.choiceConfig.choices.indexOf(defaultChoice)}`;
      }
    }

    if (option.type === 'toggle') {
      return option.toggleConfig?.defaultValue === true;
    }

    return undefined;
  };

  /**
   * Resolve the value for a dimension field from formData, evaluating a
   * computed-field dimension (e.g. geleider + kap), falling back to the
   * product option's default if not present.
   */
  const resolveDimensionValue = (optionId, data = formData) => {
    const value = data[fieldIdFor(optionId)];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    if (computedFields.some((field) => field.id === optionId)) {
      // A formula over empty inputs evaluates to 0: treat that as not filled in yet.
      const computed = resolveConstraintDimensionValue(product, optionId, data);
      return computed ? computed : undefined;
    }
    return getDefaultValueForOption(optionId);
  };

  const isNumberDimension = (optionId) =>
    matrixEngine.resolveDimensionType(product, optionId) === 'number';

  // Same tier rounding as the price calculation: 2850 is looked up as 2900.
  const roundToMatrixTier = (optionId, value) => {
    const tiers = matrixEngine.extractTiersFromCells(convertedMatrix.value, optionId);
    return tiers.length ? matrixEngine.applyTierRounding(value, tiers) : value;
  };

  /**
   * Get available values for a specific dimension based on other dimension values.
   * For a choice dimension the ids of choices that resolve to an available value
   * (by id, priceKey or label) are added, so callers can match on choice id.
   *
   * @param {string} optionId - The option ID to get available values for
   * @returns {Array} Available values for this dimension
   */
  const getAvailableValues = (optionId) => {
    if (!convertedMatrix.value) {
      return [];
    }

    const otherDimensionValues = {};
    const dimensions = getDimensionsArray();

    dimensions.forEach((dim) => {
      if (dim.optionId !== optionId) {
        const rawValue = resolveDimensionValue(dim.optionId);
        if (rawValue === undefined || rawValue === null || rawValue === '') return;
        const value = isNumberDimension(dim.optionId)
          ? roundToMatrixTier(dim.optionId, rawValue)
          : matrixEngine.resolveAxisValue(product, dim.optionId, rawValue, convertedMatrix.value);
        if (value !== undefined && value !== null && value !== '') {
          otherDimensionValues[dim.optionId] = value;
        }
      }
    });

    const availableValues = matrixEngine.getAvailableValuesForDimension(
      convertedMatrix.value,
      optionId,
      otherDimensionValues
    );

    const option = product?.options?.find((o) => o.id === optionId);
    const choices = option?.choiceConfig?.choices;
    if (!availableValues.length || !Array.isArray(choices)) return availableValues;

    const available = new Set(availableValues.map((value) => String(value).trim().toLowerCase()));
    const choiceIds = choices
      .filter((choice) => {
        const resolved = resolveChoiceAxisValue(option, choice.id, availableValues);
        return available.has(String(resolved).trim().toLowerCase());
      })
      .map((choice) => choice.id);

    return [...new Set([...availableValues, ...choiceIds])];
  };

  // Matrix axis itself, or an input of a computed axis.
  const affectsMatrix = (optionId) =>
    getDimensionsArray().some(
      (dim) =>
        dim.optionId === optionId ||
        computedFields.some(
          (cf) => cf.id === dim.optionId && cf.formula.includes(`{${optionId}}`)
        )
    );

  const numericMax = (values) => {
    const numbers = values.map((value) => parseFloat(value)).filter(Number.isFinite);
    return numbers.length ? Math.max(...numbers) : null;
  };

  /**
   * Highest value a number option can take given the other selections, or null
   * when the matrix does not limit it. Covers options that are an axis
   * themselves and options that feed a computed axis (Hoogte -> Hoogte
   * (prijsberekening)); the latter only when the formula passes the input
   * through plus a constant, so the axis maximum can be translated back.
   */
  const getNumberMax = (optionId) => {
    if (!convertedMatrix.value) return null;
    const dimensions = getDimensionsArray();

    if (dimensions.some((dim) => dim.optionId === optionId)) {
      return numericMax(getAvailableValues(optionId));
    }

    for (const dim of dimensions) {
      const field = computedFields.find((cf) => cf.id === dim.optionId);
      if (!field || !field.formula.includes(`{${optionId}}`)) continue;

      const axisAt = (value) =>
        resolveConstraintDimensionValue(product, dim.optionId, {
          ...formData,
          [fieldIdFor(optionId)]: value,
        });
      const low = axisAt(1000);
      const high = axisAt(2000);
      if (low === null || high === null || high - low !== 1000) continue;

      const axisMax = numericMax(getAvailableValues(dim.optionId));
      if (axisMax !== null) return axisMax - (low - 1000);
    }

    return null;
  };

  /**
   * Get dynamic min/max values for a dimension based on other selections
   *
   * @param {string} optionId - The option ID
   * @returns {Object|null} { min, max, availableValues } or null if no constraints
   * 
   * NOTE: Retourneert altijd null zodat de slider min/max niet wordt beperkt.
   * De matrix engine zal automatisch afronden naar dichtstbijzijnde waarde in de matrix.
   * Dit maakt het mogelijk om waarden onder de matrix minimum in te voeren (bijv. 500),
   * die dan automatisch worden afgerond naar de laagste matrix waarde (bijv. 1000).
   */
  const getDynamicMinMax = (optionId) => {
    // Retourneer null zodat de slider min/max niet wordt aangepast
    // De matrix engine zal automatisch tier rounding toepassen
    return null;
  };

  /**
   * Validate if a specific combination is available
   *
   * @param {Object} dimensionValues - Object with optionId: value mapping
   * @returns {Object} { available, error, cell }
   */
  const validateCombination = (dimensionValues) => {
    if (!convertedMatrix.value) {
      return {
        available: false,
        error: 'Matrix pricing not configured',
      };
    }

    // Build configuration object for engine
    const configuration = {};
    Object.entries(dimensionValues).forEach(([optionId, value]) => {
      const fieldId = `${product.id}-${optionId}-input`;
      configuration[fieldId] = value;
    });

    const result = matrixEngine.calculatePrice(
      {
        ...product,
        useMatrixPricing: true,
        pricingMatrix: convertedMatrix.value,
      },
      configuration
    );

    return result;
  };

  /**
   * Get validation error for a specific field value
   *
   * @param {string} optionId - The option ID
   * @param {*} value - The value to validate
   * @returns {string|null} Error message or null if valid
   */
  const getValidationError = (optionId, value) => {
    if (!convertedMatrix.value || !value || !affectsMatrix(optionId)) {
      return null;
    }

    // Build dimension values from formData with the value being validated in
    // place, so computed dimensions see it too; fall back to product defaults.
    const data = { ...formData, [fieldIdFor(optionId)]: value };
    const dimensionValues = {};
    const dimensions = getDimensionsArray();

    dimensions.forEach((dim) => {
      const resolved = resolveDimensionValue(dim.optionId, data);
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        dimensionValues[dim.optionId] = resolved;
      }
    });

    // If any dimension is still missing, skip validation (user hasn't
    // reached that option yet and there's no default to fall back on)
    const hasMissing = dimensions.some(
      (dim) => !dimensionValues[dim.optionId] &&
        dimensionValues[dim.optionId] !== 0
    );
    if (hasMissing) {
      return null;
    }

    const validation = validateCombination(dimensionValues);

    if (!validation.available) {
      return validation.error || 'This combination is not available';
    }

    return null;
  };

  return {
    convertedMatrix,
    getAvailableValues,
    getNumberMax,
    getDynamicMinMax,
    validateCombination,
    getValidationError,
  };
}
